import axios from 'axios';
import { API_ENDPOINTS, BASE_URL } from '../config/api.config';
import { LoginRequest } from '../types/auth';

interface TokenResponse {
  tokenType: string;
  accessToken: string;
  expiresIn: number;
  refreshToken: string;
  roles: string[];
  success: boolean;
  message: string;
  errors: string[];
}

class AuthService {
  private readonly TOKEN_KEYS = {
    accessToken: 'accessToken',
    refreshToken: 'refreshToken',
    expiresAt: 'expiresAt',
    userRoles: 'userRoles',
  };

  // Store tokens with expiration time tracking
  private setTokens(response: TokenResponse): void {
    console.log('💾 Guardando tokens en localStorage');

    // 🧹 LIMPIEZA: Eliminar tokens viejos del sistema anterior
    localStorage.removeItem('inoelec_admin_token');
    localStorage.removeItem('inoelec_admin_refresh_token');

    localStorage.setItem(this.TOKEN_KEYS.accessToken, response.accessToken);
    localStorage.setItem(this.TOKEN_KEYS.refreshToken, response.refreshToken);

    // Calculate and store expiration time CORREGIDO
    const expiresAt = Date.now() + response.expiresIn * 1000; // Convertir a milisegundos
    localStorage.setItem(this.TOKEN_KEYS.expiresAt, expiresAt.toString());

    // Store roles if provided
    if (response.roles) {
      localStorage.setItem(
        this.TOKEN_KEYS.userRoles,
        JSON.stringify(response.roles)
      );
    }

    console.log('✅ Tokens guardados:', {
      accessToken: !!response.accessToken,
      refreshToken: !!response.refreshToken,
      expiresAt: new Date(expiresAt).toISOString(),
      roles: response.roles,
    });
  }

  // Check if the current token is expired or will expire soon (within 1 minute)
  isTokenExpired(): boolean {
    const expiresAt = localStorage.getItem(this.TOKEN_KEYS.expiresAt);
    if (!expiresAt) {
      console.log('⏰ No hay fecha de expiración');
      return true;
    }

    // Add a buffer of 1 minute to renew token before it actually expires
    const expiryBuffer = 60 * 1000; // 1 minute in milliseconds
    const expirationTime = parseInt(expiresAt);
    const isExpired = expirationTime - expiryBuffer < Date.now();

    console.log('⏰ Verificación de expiración:', {
      expirationTime: new Date(expirationTime).toISOString(),
      now: new Date().toISOString(),
      isExpired: isExpired,
    });

    return isExpired;
  }

  // Login method
  // En authService.ts - método login
  async login(credentials: LoginRequest): Promise<TokenResponse> {
    try {
      console.log(
        '📤 Enviando credenciales de login a:',
        API_ENDPOINTS.USERS.LOGIN
      );
      const response = await axios.post<TokenResponse>(
        API_ENDPOINTS.USERS.LOGIN,
        credentials
      );

      console.log('✅ Respuesta de login recibida:', {
        success: response.data.success,
        accessToken: response.data.accessToken
          ? `${response.data.accessToken.substring(0, 20)}...`
          : 'NO TOKEN',
        expiresIn: response.data.expiresIn,
      });

      if (!response.data.accessToken) {
        throw new Error('El servidor no devolvió un token de acceso');
      }

      this.setTokens(response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Login error:', error);
      throw error;
    }
  }

  // Refresh token method
  async refreshToken(): Promise<TokenResponse | null> {
    const refreshToken = localStorage.getItem(this.TOKEN_KEYS.refreshToken);

    if (!refreshToken) {
      console.warn('⚠️ No refresh token available');
      return null;
    }

    try {
      console.log('🔄 Intentando refresh token');
      const response = await axios.post<TokenResponse>(
        API_ENDPOINTS.USERS.REFRESH,
        {
          refreshToken,
        }
      );

      this.setTokens(response.data);
      console.log('✅ Token refresh exitoso');
      return response.data;
    } catch (error) {
      console.error('❌ Token refresh error:', error);
      // If refresh token is invalid, clear auth data
      this.logout();
      return null;
    }
  }

  // Get the current access token
  getAccessToken(): string | null {
    const token = localStorage.getItem(this.TOKEN_KEYS.accessToken);
    console.log(
      '🔍 Token obtenido:',
      token ? `${token.substring(0, 10)}...` : 'null'
    );
    return token;
  }

  // Get the current user roles
  getUserRoles(): string[] {
    const rolesString = localStorage.getItem(this.TOKEN_KEYS.userRoles);
    return rolesString ? JSON.parse(rolesString) : [];
  }

  // Get logged user info
  async getLoggedUser(): Promise<any> {
    try {
      const token = this.getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }

      const response = await axios.get(`${BASE_URL}/Users/logged`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo usuario logueado:', error);
      throw error;
    }
  }

  // Logout method
  logout(): void {
    console.log('🗑️ Limpiando tokens de localStorage');

    // Limpiar tokens del sistema actual
    Object.values(this.TOKEN_KEYS).forEach(key => {
      localStorage.removeItem(key);
      console.log(`✅ Removido: ${key}`);
    });

    // 🧹 LIMPIEZA: También eliminar tokens viejos si existen
    localStorage.removeItem('inoelec_admin_token');
    localStorage.removeItem('inoelec_admin_refresh_token');
  }

  private async clearAllBrowserData(): Promise<void> {
    // 1. Cookies (sólo las accesibles desde JS)
    document.cookie.split(';').forEach(cookie => {
      const name = cookie.split('=')[0].trim();
      document.cookie = `${name}=;expires=${new Date(0).toUTCString()};path=/;`;
      // dominio explícito por si acaso
      document.cookie = `${name}=;expires=${new Date(0).toUTCString()};path=/;domain=${location.hostname};`;
    });

    // 2. Web Storage
    localStorage.clear();
    sessionStorage.clear();

    // 3. Cache Storage
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    }

    // 4. IndexedDB
    if ('indexedDB' in window && indexedDB.databases) {
      const dbs = await indexedDB.databases();
      await Promise.all(
        dbs
          .map(db => db.name)
          .filter(name => name)
          .map(name => indexedDB.deleteDatabase(name!))
      );
    }

    // 5. Service Workers
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map(reg => reg.unregister()));
    }
  }

  // Complete logout with full storage clear
  async logoutAndClear(): Promise<void> {
    localStorage.clear();
    await this.clearAllBrowserData();
    sessionStorage.clear();
    window.location.reload();
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!this.getAccessToken() && !this.isTokenExpired();
  }
}

export const authService = new AuthService();
